import { useAuth } from "../../accounts/AuthProvider";
import classes from "./LibraryMenu.module.css";
import { personalLibraryTree, libraryLabel as label } from "./libraryTree";
import { useSavedCharts } from "../../accounts/SavedChartsProvider";
import { chartErrorMessage, listLibraryCharts } from "../../accounts/chartRepository";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const ancestors = (rows, pathname, parents = []) => {
  for (const row of rows) {
    if (row.children) {
      const result = ancestors(row.children, pathname, [...parents, row.id]);
      if (result) return result;
    } else if (`/${row.slug}` === pathname) return parents;
  }
  return null;
};

const LibraryMenu = ({ onItemClicked }) => {
  const { pathname } = useLocation();
  const { client, session, error: authError } = useAuth();
  const { libraryVersion } = useSavedCharts();
  const owner = session?.user.id;
  const [personal, setPersonal] = useState({ owner: null, items: [], error: "" });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!owner || !client || authError) return;
    const controller = new AbortController();
    listLibraryCharts({ client, accessToken: session.access_token, signal: controller.signal })
      .then(items => { if (!controller.signal.aborted) setPersonal({ owner, items, error: "" }); })
      .catch(error => { if (!controller.signal.aborted) setPersonal({ owner, items: [], error: chartErrorMessage(error) }); });
    return () => controller.abort();
  }, [client, owner, session?.access_token, authError, libraryVersion, retry]);
  const tree = useMemo(() => personalLibraryTree(owner && !authError && personal.owner === owner ? personal.items : []), [owner, authError, personal]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries((ancestors(tree, pathname) || []).map(id => [id, true]))
  );
  useEffect(() => {
    setExpanded(previous => ({ ...previous,
      ...Object.fromEntries((ancestors(tree, pathname) || []).map(id => [id, true])),
    }));
  }, [pathname, tree]);

  const query = search.trim().toLowerCase();
  const matches = (row, parents = "") => {
    const name = `${parents} ${label(row.name)}`;
    return row.children
      ? row.children.some(child => matches(child, name))
      : name.toLowerCase().includes(query);
  };
  const renderRows = (rows, depth = 0, parents = "") => rows
    .filter(row => !query || matches(row, parents))
    .map(row => {
      const name = row.personal ? row.name : label(row.name);
      if (row.children) {
        const open = !!query || !!expanded[row.id];
        return <li key={row.id}>
          <button type="button" className={classes.folder} style={{ paddingLeft: 16 + depth * 16 }}
            aria-expanded={open} aria-controls={`library-group-${row.id}`}
            onClick={() => setExpanded(previous => ({ ...previous, [row.id]: !open }))}>
            <span className={classes.chevron} aria-hidden="true">{open ? "⌄" : "›"}</span>{name}
          </button>
          <ul id={`library-group-${row.id}`} hidden={!open}>
            {renderRows(row.children, depth + 1, `${parents} ${name}`)}
          </ul>
        </li>;
      }
      return <li key={row.id}><Link to={`/${row.slug}`} className={classes.item}
        style={{ paddingLeft: 36 + depth * 16 }}
        aria-current={pathname === `/${row.slug}` ? "page" : undefined}
        onClick={event => {
          if (!event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0) onItemClicked?.();
        }}>{name}{row.personal && <span className={classes.private}>Private</span>}</Link></li>;
    });

  return <nav className={classes.menu} aria-label="Chart library">
    <div className={classes.header}>
      <h2>Library</h2><p>Find your next practice chart</p>
    </div>
    {owner && personal.owner === owner && personal.error && <div className={classes.empty} role="alert">
      <p>Your saved charts could not be loaded.</p>
      <button onClick={() => setRetry(value => value + 1)}>Retry saved charts</button>
    </div>}
    <div className={classes.header}>
      <label className={classes.searchLabel}>Search charts
        <input type="search" value={search} placeholder="Scale, mode or arpeggio…"
          onChange={event => setSearch(event.target.value)} />
      </label>
    </div>
    <ul className={classes.tree}>{renderRows(tree)}</ul>
    {query && !tree.some(row => matches(row)) && <p className={classes.empty}>No charts found. Try a different name.</p>}
  </nav>;
};

export default LibraryMenu;
